import type { OwuiChatSummary } from "@lupp/shared";

export interface ChatGroup {
  label: string;
  chats: OwuiChatSummary[];
}

const MONTHS_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const DAY_MS = 24 * 60 * 60 * 1000;

function chatTimeMs(c: OwuiChatSummary): number {
  return (c.updated_at ?? c.created_at ?? 0) * 1000;
}

/**
 * Compact age of a chat's last change: s / min / h / T (Tage) / W (Wochen).
 * Older than 16 weeks → the year (e.g. "2025"). Empty when no timestamp.
 */
export function formatChatAge(c: OwuiChatSummary, nowMs: number): string {
  const ts = chatTimeMs(c);
  if (!ts) return "";
  const diff = Math.max(0, nowMs - ts);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec} s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days} T`;
  const weeks = Math.floor(days / 7);
  if (weeks < 16) return `${weeks} W`;
  return String(new Date(ts).getFullYear()); // year has no unit → no space
}

/**
 * Groups chats into OWUI-style time buckets (most-recent first):
 * Heute → Gestern → Letzte 7 Tage → Letzte 30 Tage → Monatsnamen (gleiches Jahr) → Jahre.
 * `nowMs` is injected for deterministic tests.
 */
export function groupChatsByTime(chats: OwuiChatSummary[], nowMs: number): ChatGroup[] {
  const now = new Date(nowMs);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const thisYear = now.getFullYear();

  const sorted = [...chats].sort((a, b) => chatTimeMs(b) - chatTimeMs(a));

  const bucketOf = (ts: number): string => {
    if (ts >= startOfToday) return "Heute";
    if (ts >= startOfToday - DAY_MS) return "Gestern";
    if (ts >= startOfToday - 7 * DAY_MS) return "Letzte 7 Tage";
    if (ts >= startOfToday - 30 * DAY_MS) return "Letzte 30 Tage";
    const d = new Date(ts);
    if (d.getFullYear() === thisYear) return MONTHS_DE[d.getMonth()];
    return String(d.getFullYear());
  };

  // Insertion order over descending-sorted chats yields the correct group order.
  const groups = new Map<string, OwuiChatSummary[]>();
  for (const c of sorted) {
    const label = bucketOf(chatTimeMs(c));
    const arr = groups.get(label);
    if (arr) arr.push(c);
    else groups.set(label, [c]);
  }
  return Array.from(groups, ([label, list]) => ({ label, chats: list }));
}
